import {
  getRecordingS3IntakeSettings,
  IntegrationProvider,
} from "@repo/app-types/entities/integration";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { FeatureFlagKey } from "@repo/app-types/featureFlags/keys";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { SimpleModal, SimpleModalVariant } from "@repo/ui/Modal/modal";
import { useState, type FC } from "react";
import { useNavigate, useParams } from "react-router";
import { useGenesysIntegration } from "../../../api/entities/genesysIntegration.js";
import {
  useDeleteKnowledgeBaseSyncIntegrationMutation,
  useKnowledgeBaseSyncIntegrationStatus,
} from "../../../api/entities/knowledgeBaseSyncIntegration.js";
import { useZoomContactCenterIntegration } from "../../../api/entities/zoomContactCenterIntegration.js";
import { getApiErrorMessage } from "../../../api/helpers/errors.js";
import { appPaths } from "../../../app/appPaths.js";
import { useIsFeatureFlagEnabled } from "../../../helpers/featureFlags/hooks.js";
import { Can } from "../../../helpers/teammateAuthorizationContext.js";
import { openOAuthSigninPopupForIntegration } from "../../../helpers/urlHelpers.js";
import { useAppDispatch, useAppSelector } from "../../../hooks/hook.js";
import {
  checkNiceCxoneIntegrationEnabled,
  deleteIntegration,
  selectAllIntegrations,
  selectNiceCxoneIntegration,
  selectRecordingS3IntakeIntegration,
} from "../../integrations/integrationsSlice.js";
import { settingsSections } from "../settingsSections.js";
import { useSettingsSectionsPolicy } from "../settingsSectionsPolicy.js";
import { SettingsSectionPage } from "../shared/settingsSection.js";
import { SettingsTable } from "../shared/settingsTable.js";
import { SettingsTableBodySection } from "../shared/settingsTableBodySection.js";
import { SettingsTableDataCell } from "../shared/settingsTableDataCell.js";
import { SettingsTableHeaderCell } from "../shared/settingsTableHeaderCell.js";
import { SettingsTableHeaderSection } from "../shared/settingsTableHeaderSection.js";
import { GuruIntegrationSettings } from "./guruIntegrationSettings.js";
import {
  integrationConfigs,
  NICE_CXONE_SLUG,
  RECORDING_S3_INTAKE_SLUG,
  type IntegrationConfig,
} from "./integrationConfigs.js";
import { IntegrationLogo } from "./integrationLogo.js";
import { KustomerIntegrationSettings } from "./kustomerIntegrationSettings.js";
import {
  TeamsConnectionStatus,
  TeamsIntegrationTableStatus,
  useTeamsInstalledTeams,
} from "./teamsConnectionStatus.js";

export const IntegrationSettingsSection: FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { integrationSlug } = useParams<{ integrationSlug?: string }>();
  const { sections } = useSettingsSectionsPolicy();
  const allIntegrations = useAppSelector(selectAllIntegrations);
  // The intake row is the Five9 or the RingCentral provider, so it is looked
  // up by slug rather than through `integrationsByProvider`.
  const recordingS3IntakeIntegration = useAppSelector(
    selectRecordingS3IntakeIntegration,
  );
  const niceCxoneIntegration = useAppSelector(selectNiceCxoneIntegration);
  const isTeamsEnabled = useIsFeatureFlagEnabled(FeatureFlagKey.MicrosoftTeams);
  const isZoomContactCenterEnabled = useIsFeatureFlagEnabled(
    FeatureFlagKey.ZoomCcIntegration,
  );
  const [disconnectTarget, setDisconnectTarget] =
    useState<IntegrationConfig | null>(null);
  const [disconnectError, setDisconnectError] = useState<string | null>(null);

  const integrationsByProvider = new Map(
    allIntegrations.map((integration) => [integration.provider, integration]),
  );

  // Guru's state comes from its own route rather than the boot payload: the
  // stored settings carry an encrypted token per collection, which no customer
  // response may include, so the API returns a non-secret view instead.
  // Fetched whenever this surface is available, then shared by the overview
  // row and the detail form.
  const isGuruDetail =
    integrationSlug === "guru" && sections.knowledgeBases.canAccess;
  const isKustomerDetail =
    integrationSlug === "kustomer" && sections.knowledgeBases.canAccess;
  const isKnowledgeBaseSyncDetail = isGuruDetail || isKustomerDetail;
  const guruStatus = useKnowledgeBaseSyncIntegrationStatus(
    "guru",
    sections.knowledgeBases.canAccess,
  );
  const kustomerStatus = useKnowledgeBaseSyncIntegrationStatus(
    "kustomer",
    sections.knowledgeBases.canAccess,
  );
  const [deleteKnowledgeBaseSyncIntegration] =
    useDeleteKnowledgeBaseSyncIntegrationMutation();
  const guruIntegration =
    guruStatus.currentData?.integration?.provider === "guru"
      ? guruStatus.currentData.integration
      : null;
  const kustomerIntegration =
    kustomerStatus.currentData?.integration?.provider === "kustomer"
      ? kustomerStatus.currentData.integration
      : null;

  // Genesys reads from its own route for the same reason Guru does — its
  // settings carry the encrypted client secret. Always fetched: this section
  // only renders for non-recruiting companies, all of which see the Genesys row
  // and its live/paused status. The detail page (connect / status / disconnect)
  // lives on its own tabbed route (GenesysIntegrationDetailSection).
  const genesysStatus = useGenesysIntegration(true);
  const genesysIntegration = genesysStatus.currentData?.integration ?? null;

  // Zoom Contact Center reads from its own route for the same reason Genesys
  // does — its settings carry the encrypted client secret. Fetched whenever the
  // flag is on so the overview row can say whether the sync is live or paused;
  // the Zoom CC detail page lives on its own tabbed route
  // (ZoomContactCenterIntegrationDetailSection), not the shared detail view.
  const zoomContactCenterStatus = useZoomContactCenterIntegration(
    isZoomContactCenterEnabled,
  );
  const zoomContactCenterIntegration =
    zoomContactCenterStatus.currentData?.integration ?? null;

  const getIntegrationId = (provider: IntegrationProvider): string | null => {
    if (provider === IntegrationProvider.GURU) {
      return guruIntegration?.integration_id ?? null;
    }
    if (provider === IntegrationProvider.KUSTOMER) {
      return kustomerIntegration?.integration_id ?? null;
    }
    if (provider === IntegrationProvider.GENESYS) {
      return genesysIntegration?.id ?? null;
    }
    if (provider === IntegrationProvider.ZOOM_CONTACT_CENTER) {
      return zoomContactCenterIntegration?.id ?? null;
    }
    // The "Recording S3 intake" config is keyed on the Five9 provider but
    // stands for either intake provider; the row may be the RingCentral one.
    if (provider === IntegrationProvider.FIVE9_RECORDING_S3_INTAKE) {
      return recordingS3IntakeIntegration?.id ?? null;
    }
    return integrationsByProvider.get(provider)?.id ?? null;
  };

  // Fetched once here and shared by the overview table's Teams status cell
  // and the Teams detail card, so navigating between them reuses the result.
  const teamsIntegration =
    integrationsByProvider.get(IntegrationProvider.TEAMS) ?? null;
  const { teamsWithInstalledAppFetch, retryFetch } = useTeamsInstalledTeams(
    isTeamsEnabled ? (teamsIntegration?.id ?? null) : null,
  );

  const visibleIntegrationConfigs = integrationConfigs.filter(
    (config) =>
      (config.provider !== IntegrationProvider.TEAMS || isTeamsEnabled) &&
      (config.provider !== IntegrationProvider.ZOOM_CONTACT_CENTER ||
        isZoomContactCenterEnabled) &&
      // The S3-intake row shows only when the boot payload carries one (i.e.
      // the company is an intake customer — the row is created from HQ).
      (config.slug !== RECORDING_S3_INTAKE_SLUG ||
        recordingS3IntakeIntegration !== null) &&
      // Likewise NICE CXone, which Alpharun sets up by script.
      (config.slug !== NICE_CXONE_SLUG || niceCxoneIntegration !== null) &&
      ((config.provider !== IntegrationProvider.GURU &&
        config.provider !== IntegrationProvider.KUSTOMER) ||
        sections.knowledgeBases.canAccess),
  );

  const selectedIntegration = integrationConfigs.find(
    (config) =>
      config.slug === integrationSlug &&
      ((config.provider !== IntegrationProvider.GURU &&
        config.provider !== IntegrationProvider.KUSTOMER) ||
        isKnowledgeBaseSyncDetail) &&
      (config.provider !== IntegrationProvider.ZOOM_CONTACT_CENTER ||
        isZoomContactCenterEnabled),
  );

  const renderConnectedStatus = () => (
    <span className="text-xs font-medium text-success">Connected</span>
  );
  // A connection whose sync the customer (or, for S3 intake, Alpharun) has
  // paused — the one state the overview should not hide behind "Connected".
  const renderPausedStatus = () => (
    <span className="text-xs font-medium text-amber-500">Paused</span>
  );

  /** Disconnects through the provider's canonical customer route. */
  const handleDisconnect = async (config: IntegrationConfig) => {
    setDisconnectError(null);

    const integrationId = getIntegrationId(config.provider);
    if (!integrationId) return;

    try {
      switch (config.provider) {
        case IntegrationProvider.GURU:
        case IntegrationProvider.KUSTOMER:
          await deleteKnowledgeBaseSyncIntegration({
            provider: config.provider,
            integrationId,
          }).unwrap();
          break;
        default:
          // Still boot-backed, so it disconnects through the Redux thunk, which
          // keeps its own error state rather than surfacing one here.
          dispatch(deleteIntegration(integrationId));
      }
    } catch (error) {
      setDisconnectError(
        getApiErrorMessage(
          error,
          `Could not disconnect ${config.name} — please try again.`,
        ),
      );
    }
  };

  const renderIntegrationAction = (config: IntegrationConfig) => {
    // The S3-intake and NICE CXone integrations have no connect/disconnect —
    // Alpharun creates them (from HQ, by script), and the generic delete route
    // refuses them.
    if (
      config.slug === RECORDING_S3_INTAKE_SLUG ||
      config.slug === NICE_CXONE_SLUG
    ) {
      return null;
    }

    if (getIntegrationId(config.provider)) {
      return (
        <Can
          I={PermissionActions.DELETE}
          a={PermissionSubjects.INTEGRATIONS}
          passThrough={true}
        >
          {(allowed) => (
            <Button
              size={Size.SMALL}
              variant={ButtonVariant.Warning}
              isDisabled={!allowed}
              onClick={() => setDisconnectTarget(config)}
            >
              Disconnect
            </Button>
          )}
        </Can>
      );
    }

    if (
      config.provider !== IntegrationProvider.SLACK &&
      config.provider !== IntegrationProvider.TEAMS
    ) {
      return null;
    }

    return (
      <Can
        I={PermissionActions.CREATE}
        a={PermissionSubjects.INTEGRATIONS}
        passThrough={true}
      >
        {(allowed) => (
          <Button
            size={Size.SMALL}
            variant={ButtonVariant.Primary}
            isDisabled={!allowed}
            onClick={() => openOAuthSigninPopupForIntegration(config.provider)}
          >
            Connect
          </Button>
        )}
      </Can>
    );
  };

  const renderIntegrationTableStatus = (config: IntegrationConfig) => {
    // The S3-intake row reads "Connected" while its `enabled` switch is on and
    // "Paused" when Alpharun has paused intake for the company — the overview
    // should not hide that state behind a green label.
    if (config.slug === RECORDING_S3_INTAKE_SLUG) {
      if (!recordingS3IntakeIntegration) {
        return null;
      }
      return getRecordingS3IntakeSettings(recordingS3IntakeIntegration)?.enabled
        ? renderConnectedStatus()
        : renderPausedStatus();
    }

    // NICE CXone likewise: "Paused" while Alpharun has paused the import.
    if (config.slug === NICE_CXONE_SLUG) {
      if (!niceCxoneIntegration) {
        return null;
      }
      return checkNiceCxoneIntegrationEnabled(niceCxoneIntegration)
        ? renderConnectedStatus()
        : renderPausedStatus();
    }

    // Genesys connects by entering credentials on its detail page rather than
    // through an OAuth popup, so its overview button navigates there.
    if (config.provider === IntegrationProvider.GENESYS) {
      if (!genesysIntegration) {
        return (
          <Can
            I={PermissionActions.CREATE}
            a={PermissionSubjects.INTEGRATIONS}
            passThrough={true}
          >
            {(allowed) => (
              <Button
                size={Size.SMALL}
                variant={ButtonVariant.Primary}
                isDisabled={!allowed}
                onClick={() =>
                  navigate(
                    appPaths.settingsIntegration.build({
                      integrationSlug: config.slug,
                    }),
                  )
                }
              >
                Connect
              </Button>
            )}
          </Can>
        );
      }
      // Reads "Connected" like every other row when it is working normally, and
      // only diverges for the state the others cannot be in — a connection whose
      // sync the customer has paused, which the overview should not hide.
      return genesysIntegration.settings.is_enabled
        ? renderConnectedStatus()
        : renderPausedStatus();
    }

    // Zoom Contact Center connects by entering credentials on its detail page
    // rather than through an OAuth popup, so its overview button navigates there.
    if (config.provider === IntegrationProvider.ZOOM_CONTACT_CENTER) {
      if (!zoomContactCenterIntegration) {
        return (
          <Can
            I={PermissionActions.CREATE}
            a={PermissionSubjects.INTEGRATIONS}
            passThrough={true}
          >
            {(allowed) => (
              <Button
                size={Size.SMALL}
                variant={ButtonVariant.Primary}
                isDisabled={!allowed}
                onClick={() =>
                  navigate(
                    appPaths.settingsIntegration.build({
                      integrationSlug: config.slug,
                    }),
                  )
                }
              >
                Connect
              </Button>
            )}
          </Can>
        );
      }
      // Reads "Connected" like every other row when it is working normally, and
      // only diverges for the state the others cannot be in — a connection whose
      // sync the customer has paused, which the overview should not hide.
      return zoomContactCenterIntegration.settings.is_enabled
        ? renderConnectedStatus()
        : renderPausedStatus();
    }

    if (!getIntegrationId(config.provider)) {
      return renderIntegrationAction(config);
    }
    if (config.provider === IntegrationProvider.TEAMS) {
      return (
        <TeamsIntegrationTableStatus
          installedTeamsFetch={teamsWithInstalledAppFetch}
        />
      );
    }
    return renderConnectedStatus();
  };

  // Connected integrations first, then alphabetically by name within each group.
  const sortedIntegrations = [...visibleIntegrationConfigs].sort(
    (firstIntegration, secondIntegration) =>
      Number(Boolean(getIntegrationId(secondIntegration.provider))) -
        Number(Boolean(getIntegrationId(firstIntegration.provider))) ||
      firstIntegration.name.localeCompare(secondIntegration.name),
  );

  const renderOverview = () => (
    <div className="space-y-6">
      <SettingsTable>
        <SettingsTableHeaderSection>
          <tr>
            <SettingsTableHeaderCell className="w-full">
              Integration
            </SettingsTableHeaderCell>
            <SettingsTableHeaderCell className="w-32">
              Status
            </SettingsTableHeaderCell>
          </tr>
        </SettingsTableHeaderSection>
        <SettingsTableBodySection>
          {sortedIntegrations.map((integration) => (
            <tr
              key={integration.slug}
              className="cursor-pointer hover:bg-muted"
              role="link"
              tabIndex={0}
              onClick={() =>
                navigate(
                  appPaths.settingsIntegration.build({
                    integrationSlug: integration.slug,
                  }),
                )
              }
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  navigate(
                    appPaths.settingsIntegration.build({
                      integrationSlug: integration.slug,
                    }),
                  );
                }
              }}
            >
              <SettingsTableDataCell>
                <div className="flex items-center gap-2.5">
                  <IntegrationLogo provider={integration.provider} />
                  <div className="font-semibold">{integration.name}</div>
                </div>
              </SettingsTableDataCell>
              <SettingsTableDataCell className="whitespace-nowrap">
                <div
                  onClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => event.stopPropagation()}
                >
                  {renderIntegrationTableStatus(integration)}
                </div>
              </SettingsTableDataCell>
            </tr>
          ))}
        </SettingsTableBodySection>
      </SettingsTable>
    </div>
  );

  const renderDetail = (integration: IntegrationConfig) => {
    const integrationId = getIntegrationId(integration.provider);

    return (
      <div className="space-y-6">
        {/* The logo sits next to the integration name in the page header; the
            detail body leads with just the description. */}
        <p className="max-w-3xl text-sm leading-6 text-muted-foreground">
          {integration.description}
        </p>

        {/* Teams connects in two parts (consent + org-wide app install), so its
            detail page breaks the status down instead of a bare "Connected". */}
        {integration.provider === IntegrationProvider.TEAMS && integrationId ? (
          <TeamsConnectionStatus
            integrationId={integrationId}
            installedTeamsFetch={teamsWithInstalledAppFetch}
            onRetryInstalledTeams={retryFetch}
          />
        ) : null}

        {/* Guru is configured, not just connected: the collections to sync and
            their tokens are entered here, both to set it up and to change it. */}
        {integration.provider === IntegrationProvider.GURU ? (
          <GuruIntegrationSettings
            integration={guruIntegration}
            activeSyncRun={guruStatus.currentData?.active_sync_run ?? null}
            isLoading={!guruStatus.currentData && !guruStatus.isError}
            isError={guruStatus.isError && !guruStatus.currentData}
            onReload={() => void guruStatus.refetch()}
          />
        ) : null}
        {integration.provider === IntegrationProvider.KUSTOMER ? (
          <KustomerIntegrationSettings
            integration={kustomerIntegration}
            activeSyncRun={kustomerStatus.currentData?.active_sync_run ?? null}
            isLoading={!kustomerStatus.currentData && !kustomerStatus.isError}
            isError={kustomerStatus.isError && !kustomerStatus.currentData}
            onReload={() => void kustomerStatus.refetch()}
          />
        ) : null}

        {/* Genesys detail is not rendered here — it has its own tabbed route
            (GenesysIntegrationDetailSection). This shared detail view only
            covers Slack, Teams, Guru and Kustomer. */}
      </div>
    );
  };

  return (
    <SettingsSectionPage
      title={
        selectedIntegration ? (
          <span className="flex min-w-0 items-center gap-2">
            <IntegrationLogo provider={selectedIntegration.provider} />
            <span className="truncate">{selectedIntegration.name}</span>
          </span>
        ) : (
          settingsSections.integrations.label
        )
      }
      actions={
        selectedIntegration && getIntegrationId(selectedIntegration.provider)
          ? renderIntegrationAction(selectedIntegration)
          : undefined
      }
    >
      {/* A refused disconnect is the customer's to act on (a sync is running,
          or its knowledge bases still need resolving), so it is said out here
          rather than swallowed with the modal. */}
      {disconnectError ? (
        <p className="mb-4 text-sm text-red-600">{disconnectError}</p>
      ) : null}

      {selectedIntegration
        ? renderDetail(selectedIntegration)
        : renderOverview()}

      <SimpleModal
        isOpen={disconnectTarget !== null}
        onCancel={() => setDisconnectTarget(null)}
        variant={SimpleModalVariant.Warning}
        title={disconnectTarget ? `Disconnect ${disconnectTarget.name}?` : ""}
        subtitle={disconnectTarget?.disconnectWarning}
        confirmButtonText="Disconnect"
        onConfirm={() => {
          if (disconnectTarget) {
            void handleDisconnect(disconnectTarget);
          }

          setDisconnectTarget(null);
        }}
      />
    </SettingsSectionPage>
  );
};
